import { gql } from '@apollo/client';

// Hand-typed GraphQL operations for AI-suggested quote carousel slides. There
// is no codegen for this feature — production introspection is disabled — so
// these types must be kept in exact sync with the backend SDL by hand:
//
//   type QuoteSuggestionQuote { text: String! start: Float! end: Float! }
//   type QuoteSuggestionJob { id: ID! processing_status: String! quotes: [QuoteSuggestionQuote!]! error_code: String createdAt: String }
//   type QuoteSuggestionQuota { used: Int! limit: Int! resets_at: String! }
//   generateMyQuoteSuggestions(clipId: ID!): QuoteSuggestionJob!
//   myQuoteSuggestionJob(id: ID!): QuoteSuggestionJob
//   myQuoteSuggestionQuota: QuoteSuggestionQuota!

export interface QuoteSuggestionQuoteDto {
  text: string;
  start: number;
  end: number;
}

export interface QuoteSuggestionJobDto {
  id: string;
  processing_status: 'pending' | 'processing' | 'done' | 'failed';
  error_code: string | null;
  quotes: QuoteSuggestionQuoteDto[];
}

export interface QuoteSuggestionQuotaDto {
  used: number;
  limit: number;
  resets_at: string;
}

const QUOTE_SUGGESTION_JOB_FIELDS = `
  id
  processing_status
  error_code
  quotes {
    text
    start
    end
  }
`;

export const GENERATE_MY_QUOTE_SUGGESTIONS = gql`
  mutation GenerateMyQuoteSuggestions($clipId: ID!) {
    generateMyQuoteSuggestions(clipId: $clipId) {
      ${QUOTE_SUGGESTION_JOB_FIELDS}
    }
  }
`;

export interface GenerateMyQuoteSuggestionsData {
  generateMyQuoteSuggestions: QuoteSuggestionJobDto;
}

export interface GenerateMyQuoteSuggestionsVars {
  clipId: string;
}

export const MY_QUOTE_SUGGESTION_JOB = gql`
  query MyQuoteSuggestionJob($id: ID!) {
    myQuoteSuggestionJob(id: $id) {
      ${QUOTE_SUGGESTION_JOB_FIELDS}
    }
  }
`;

export interface MyQuoteSuggestionJobData {
  myQuoteSuggestionJob: QuoteSuggestionJobDto | null;
}

export interface MyQuoteSuggestionJobVars {
  id: string;
}

export const MY_QUOTE_SUGGESTION_QUOTA = gql`
  query MyQuoteSuggestionQuota {
    myQuoteSuggestionQuota {
      used
      limit
      resets_at
    }
  }
`;

export interface MyQuoteSuggestionQuotaData {
  myQuoteSuggestionQuota: QuoteSuggestionQuotaDto;
}

// Source video of the clip's parent, for the carousel's "Vídeo original"
// frames. Lightweight on purpose (the editor queries select every clip): only
// the four URL fields `resolvePreviewVideoUrl` reads, all present on both
// `MyProject` and `MyLibrary` (see GET_MY_PROJECT / GET_MY_LIBRARY).
const SOURCE_VIDEO_FIELDS = `
  id
  base_video_url
  compressed_base_video_url
  cut_video_url
  compressed_cut_video_url
`;

export interface QuoteCarouselSourceVideoDto {
  id: string;
  base_video_url?: string | null;
  compressed_base_video_url?: string | null;
  cut_video_url?: string | null;
  compressed_cut_video_url?: string | null;
}

export const QUOTE_CAROUSEL_PROJECT_SOURCE = gql`
  query QuoteCarouselProjectSource($id: ID!) {
    myProject(id: $id) {
      ${SOURCE_VIDEO_FIELDS}
    }
  }
`;

export interface QuoteCarouselProjectSourceData {
  myProject: QuoteCarouselSourceVideoDto | null;
}

export const QUOTE_CAROUSEL_LIBRARY_SOURCE = gql`
  query QuoteCarouselLibrarySource($id: ID!) {
    myLibrary(id: $id) {
      ${SOURCE_VIDEO_FIELDS}
    }
  }
`;

export interface QuoteCarouselLibrarySourceData {
  myLibrary: QuoteCarouselSourceVideoDto | null;
}

export interface QuoteCarouselSourceVars {
  id: string;
}
